import { type SupabaseClient, createClient } from '@supabase/supabase-js'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

/**
 * Singleton holder. We DO NOT throw at module load — a top-level throw blew
 * up the SPA before React mounted, which meant the ErrorBoundary never got a
 * chance to render. Instead we surface the missing-env condition lazily, the
 * first time anything actually touches the client. The error then propagates
 * into a use-site try/catch (or React's render-phase error boundary) and
 * users see a real fallback UI rather than a blank page.
 */
let _client: SupabaseClient | null = null
let _envChecked = false
let _envError: Error | null = null

function getEnvError(): Error | null {
  if (_envChecked) return _envError
  _envChecked = true
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    _envError = new Error(
      'Missing Supabase env vars: VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY. ' +
        'Set them in your .env.local before launching the SPA.',
    )
    // Helpful breadcrumb for ops without crashing the bundle.
    // eslint-disable-next-line no-console
    console.error('[supabase] env not configured:', _envError.message)
  }
  return _envError
}

/**
 * Lazy accessor. First call validates env + creates the singleton. Subsequent
 * calls return the cached client. Throws on missing env so callers (auth
 * provider, realtime hooks) propagate to ErrorBoundary instead of a blank
 * page.
 */
export function getSupabase(): SupabaseClient {
  const err = getEnvError()
  if (err) throw err
  if (_client) return _client
  _client = createClient(SUPABASE_URL as string, SUPABASE_ANON_KEY as string, {
    auth: {
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      autoRefreshToken: true,
    },
  })
  // Test-mode: expose the supabase client so e2e specs running against the
  // real project can drive password login from the browser context. Gated on
  // __OHEAR_TEST_MODE__ so production bundles never see a global handle.
  if (
    typeof window !== 'undefined' &&
    (window as unknown as { __OHEAR_TEST_MODE__?: boolean }).__OHEAR_TEST_MODE__ === true
  ) {
    ;(window as unknown as { __OHEAR_SUPABASE__: SupabaseClient }).__OHEAR_SUPABASE__ = _client
  }
  return _client
}

/**
 * Backward-compatible singleton export. Existing call sites do
 * `supabase.auth.getSession()`, `supabase.channel(...)`, etc. — we proxy
 * every property access through `getSupabase()` so the underlying client is
 * created lazily but the call site doesn't need to change.
 *
 * If env is missing, accessing any property throws a clear error that the
 * use-site try/catch (or React ErrorBoundary) catches.
 */
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, prop, receiver) {
    const client = getSupabase()
    const value = Reflect.get(client as unknown as object, prop, receiver)
    return typeof value === 'function' ? value.bind(client) : value
  },
})
